import { Injectable } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { map, catchError, switchMap, withLatestFrom, mergeMap, concatMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  DeleteNotificationContentAction,
  DeleteNotificationContentCancelledAction,
  DeleteNotificationContentClickedAction,
  DeleteNotificationContentErroredAction,
  DeleteNotificationContentSucceededAction,
  NotificationContentLoadAction,
  NotificationContentLoadErroredAction,
  NotificationContentLoadSucceededAction,
  NotificationContentPaginationChangedAction,
  NotificationContentSortChangedAction,
  PublishNotificationContentAction,
  PublishNotificationContentCancelledAction,
  PublishNotificationContentClickedAction,
  PublishNotificationContentErroredAction,
  PublishNotificationContentSucceededAction,
} from './notification-content.actions';
import { NotificationContentService } from './notification-content.service';
import { NotificationContentError } from './notification-content.errors';
import { AppStore } from '../store.model';
import {
  getNotificationContentById,
  getNotificationContentQuery,
} from './notification-content.selectors';
import { ConfirmationDialogService } from 'studio-components';
import { getNotificationContentErrorType } from './helpers/get-notification-content-error-type';

@Injectable()
export class NotificationContentEffects {
  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        NotificationContentLoadAction,
        NotificationContentPaginationChangedAction,
        NotificationContentSortChangedAction,
        DeleteNotificationContentSucceededAction,
        PublishNotificationContentSucceededAction,
      ),
      withLatestFrom(this.store.select(getNotificationContentQuery)),
      switchMap(([, query]) =>
        this.notificationContentService.load(query).pipe(
          map((response) => NotificationContentLoadSucceededAction(response)),
          catchError((err) =>
            of(
              NotificationContentLoadErroredAction({
                apiError: new NotificationContentError(
                  getNotificationContentErrorType(err, 'load'),
                  err,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteNotificationContentAction),
      mergeMap(({ notificationContentId }) =>
        this.notificationContentService.delete(notificationContentId).pipe(
          map(() => DeleteNotificationContentSucceededAction()),
          catchError((err) =>
            of(
              DeleteNotificationContentErroredAction({
                apiError: new NotificationContentError(
                  getNotificationContentErrorType(err, 'delete'),
                  err,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // When delete is clicked
  confirmDeleteNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteNotificationContentClickedAction),
      mergeMap(({ notificationContentId }) =>
        of(notificationContentId).pipe(
          withLatestFrom(this.store.select(getNotificationContentById(notificationContentId))),
        ),
      ),
      concatMap(([notificationContentId, notificationContent]) =>
        this.confirmationService
          .open({
            title: `Are you sure you want to delete "${notificationContent.body.title}"?`,
          })
          .afterClosed()
          .pipe(
            map((result) =>
              result
                ? DeleteNotificationContentAction({ notificationContentId })
                : DeleteNotificationContentCancelledAction(),
            ),
          ),
      ),
    ),
  );

  // When publish is clicked
  confirmPublishNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PublishNotificationContentClickedAction),
      mergeMap(({ notificationContentId }) =>
        of(notificationContentId).pipe(
          withLatestFrom(this.store.select(getNotificationContentById(notificationContentId))),
        ),
      ),
      concatMap(([notificationContentId, notificationContent]) =>
        this.confirmationService
          .open({
            title: `Do you want to publish "${notificationContent.body.title}"?`,
          })
          .afterClosed()
          .pipe(
            map((result) =>
              result
                ? PublishNotificationContentAction({ notificationContentId })
                : PublishNotificationContentCancelledAction(),
            ),
          ),
      ),
    ),
  );

  publish$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PublishNotificationContentAction),
      concatMap(({ notificationContentId }) =>
        this.notificationContentService.publish(notificationContentId).pipe(
          map(() => PublishNotificationContentSucceededAction()),
          catchError((err) =>
            of(
              PublishNotificationContentErroredAction({
                apiError: new NotificationContentError(
                  getNotificationContentErrorType(err, 'publish'),
                  err,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private notificationContentService: NotificationContentService,
    private store: Store<AppStore>,
    private confirmationService: ConfirmationDialogService,
  ) {}
}
